import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { leadApi, engageApi, eventModelApi, userPrefsApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLabel } from "@/contexts/LabelContext";
import {
  Target, ChevronRight, Search, Filter, Loader2, Plus,
  X, ChevronDown, Calendar, MapPin, Users, DollarSign,
  Users2, Shield, Columns,
} from "lucide-react";
import { LEADS } from "@/constants/testIds";
import { toast } from "sonner";
import { leadStatusLabel, qualStatusLabel } from "../utils/leadStatusLabels";

const STATUSES = ["", "NEW", "CONTACTED", "QUALIFYING", "QUALIFIED", "NURTURE", "POC_REVIEW", "CONVERTED", "DISQUALIFIED", "CLOSED"];
const QUALIFICATIONS = ["", "UNQUALIFIED", "PARTIALLY_QUALIFIED", "QUALIFIED", "POC_REVIEW_REQUIRED"];
const SOURCES = ["", "WEB", "EMAIL", "MANUAL", "WHATSAPP"];

// CRM-UX-01 Stage 03 — Personalized column registry
const LEADS_COL_REGISTRY = [
  { id: "lead_number",          label: "Lead #",       defaultVisible: true,  alwaysVisible: true  },
  { id: "customer_name",        label: "Customer",     defaultVisible: true,  alwaysVisible: false },
  { id: "event_type_interest",  label: "Event",        defaultVisible: true,  alwaysVisible: false },
  { id: "status",               label: "Status",       defaultVisible: true,  alwaysVisible: true  },
  { id: "qualification_status", label: "Qualification",defaultVisible: true,  alwaysVisible: false },
  { id: "qualification_score",  label: "Score",        defaultVisible: false, alwaysVisible: false },
  { id: "source_channel",       label: "Source",       defaultVisible: true,  alwaysVisible: false },
  { id: "location",             label: "Location",     defaultVisible: false, alwaysVisible: false },
  { id: "event_date",           label: "Event Date",   defaultVisible: false, alwaysVisible: false },
  { id: "guest_count",          label: "Guest Count",  defaultVisible: false, alwaysVisible: false },
  { id: "budget_range",         label: "Budget",       defaultVisible: false, alwaysVisible: false },
  { id: "assigned_poc_name",    label: "Assigned RM",  defaultVisible: false, alwaysVisible: false },
  { id: "last_interaction_at",  label: "Last Activity",defaultVisible: true,  alwaysVisible: false },
];
const DEFAULT_LEAD_COLS = LEADS_COL_REGISTRY.filter(c => c.defaultVisible || c.alwaysVisible).map(c => c.id);

const STATUS_PILL = {
  NEW: "text-sky-400 bg-sky-400/10 border-sky-400/20",
  CONTACTED: "text-[#0176D3] bg-blue-400/10 border-blue-400/20",
  QUALIFYING: "text-indigo-400 bg-indigo-400/10 border-indigo-400/20",
  QUALIFIED: "text-[#2E844A] bg-emerald-400/10 border-emerald-400/20",
  NURTURE: "text-[#8C4B00] bg-amber-400/10 border-amber-400/20",
  POC_REVIEW: "text-rose-400 bg-rose-400/10 border-rose-400/20",
  CONVERTED: "text-[#2E844A] bg-teal-400/10 border-teal-400/20",
  DISQUALIFIED: "text-[#706E6B] bg-slate-500/10 border-slate-500/20",
  CLOSED: "text-[#706E6B] bg-[#E5E5E5]/10 border-[#C9C9C9]/20",
};

const QUAL_PILL = {
  QUALIFIED: "text-[#2E844A]",
  PARTIALLY_QUALIFIED: "text-[#8C4B00]",
  UNQUALIFIED: "text-[#706E6B]",
  POC_REVIEW_REQUIRED: "text-rose-400",
};

export default function Leads() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const moduleLabel = useLabel("crm.module.leads", "Leads");

  // CRM-UX-01 Stage 03: personalized columns
  const [visibleCols,     setVisibleCols]     = useState(DEFAULT_LEAD_COLS);
  const [showColPicker,   setShowColPicker]   = useState(false);
  const [pendingCols,     setPendingCols]      = useState(DEFAULT_LEAD_COLS);
  const [colPrefSaving,   setColPrefSaving]   = useState(false);

  // CRM-UX-03: scope default — RM/EPD start with "My Leads"; BO/supervisors start with "All"
  const _supervisorRoles = ["BUSINESS_OWNER", "CRM_MANAGER", "TENANT_ADMIN", "PLATFORM_ADMIN", "FINANCE_APPROVER", "FINANCE_MANAGER"];
  const defaultScope = _supervisorRoles.includes(user?.role) ? "all" : "my";

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState(defaultScope);
  // debouncedSearch drives backend fuzzy query; search drives the input value
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [eventTypes, setEventTypes] = useState([]);
  const [filters, setFilters] = useState(() => ({
    status:               new URLSearchParams(location.search).get("status") || "",
    qualification:        "",
    source:               "",
    event_type_interest:  "",
    planning_scope:       "",
  }));
  const [page, setPage] = useState(0);
  const [showNewLead, setShowNewLead] = useState(false);
  const PAGE_SIZE = 20;

  // Sync status filter when URL query param changes (e.g. pipeline drill-through)
  useEffect(() => {
    const urlStatus = new URLSearchParams(location.search).get("status") || "";
    setFilters(f => f.status === urlStatus ? f : { ...f, status: urlStatus });
    setPage(0);
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce search input → reset to page 0 on new query
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Load event types once (for filter dropdown)
  useEffect(() => {
    eventModelApi.listEventTypes()
      .then(r => setEventTypes((r.data || []).filter(t => t.status === "ACTIVE" || t.is_active)))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
        ...(filters.status               && { status: filters.status }),
        ...(filters.qualification        && { qualification: filters.qualification }),
        ...(filters.source               && { source: filters.source }),
        ...(filters.event_type_interest  && { event_type_interest: filters.event_type_interest }),
        ...(filters.planning_scope       && { planning_scope: filters.planning_scope }),
        // Fuzzy backend search: name, phone, email, lead# — searches both customer_name
        // and legacy contact_name so provisional BME leads are always findable
        ...(debouncedSearch              && { q: debouncedSearch }),
        // CRM-UX-03: scope filter
        scope,
      };
      const { data } = await leadApi.list(params);
      setLeads(data.results || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [filters, page, debouncedSearch, scope]);

  useEffect(() => { load(); }, [load]);

  // CRM-UX-01 Stage 03: load saved column preferences on mount
  useEffect(() => {
    userPrefsApi.getListView("leads")
      .then(({ data }) => {
        if (data?.columns?.length) {
          setVisibleCols(data.columns);
          setPendingCols(data.columns);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When search is active, results already come from the backend fuzzy query.
  // Client-side pass-through keeps pagination/filter composition consistent.
  const filtered = leads;

  const handleLeadCreated = (lead) => {
    load();
    navigate(`/engage/leads/${lead.id}`);
  };

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Target className="w-5 h-5 text-[#0176D3]" />
          <h1 className="text-xl font-bold text-[#181818] font-sans" data-testid="leads-page-heading">{moduleLabel}</h1>
          <span className="text-xs font-mono text-[#706E6B] bg-[#F3F3F3] border border-[#E5E5E5] px-2 py-0.5 rounded">
            {total} total
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* CRM-UX-03: Scope toggle — show for all roles (BO defaults to "All") */}
          <div
            data-testid="leads-scope-toggle"
            className="flex items-center bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg overflow-hidden text-xs"
          >
            <button
              data-testid="leads-scope-my"
              onClick={() => { setScope("my"); setPage(0); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors font-medium ${
                scope === "my"
                  ? "bg-[#0176D3] text-white"
                  : "text-[#444444] hover:text-[#0176D3]"
              }`}
            >
              <Users className="w-3 h-3" />
              {_supervisorRoles.includes(user?.role) ? "My Leads" : "My Leads"}
            </button>
            <button
              data-testid="leads-scope-all"
              onClick={() => { setScope("all"); setPage(0); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 transition-colors font-medium ${
                scope === "all"
                  ? "bg-[#0176D3] text-white"
                  : "text-[#444444] hover:text-[#0176D3]"
              }`}
            >
              {user?.role === "BUSINESS_OWNER" && <Shield className="w-3 h-3" />}
              All {user?.role === "BUSINESS_OWNER" ? "Authorized" : ""}
            </button>
          </div>

          <button
            data-testid="new-lead-btn"
            onClick={() => setShowNewLead(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-xs text-[#0176D3] hover:bg-[#EEF4FF] transition-colors font-medium"
          >
            <Plus className="w-3.5 h-3.5" /> New Lead
          </button>

          {/* CRM-UX-01 Stage 03: Column picker */}
          <div className="relative">
            <button
              data-testid="leads-column-picker-btn"
              onClick={() => { setShowColPicker(v => !v); setPendingCols(visibleCols); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E5E5] rounded-lg text-xs text-[#706E6B] hover:text-[#181818] hover:border-slate-500 transition-colors"
            >
              <Columns className="w-3.5 h-3.5" /> Columns
            </button>
            {showColPicker && (
              <div className="absolute right-0 top-8 z-40 bg-white border border-[#E5E5E5] rounded-xl shadow-lg p-4 w-56 space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#706E6B] mb-2">Visible Columns</p>
                {LEADS_COL_REGISTRY.map(col => (
                  <label key={col.id} className="flex items-center gap-2 text-xs text-[#444444] cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`col-toggle-${col.id}`}
                      checked={pendingCols.includes(col.id)}
                      disabled={col.alwaysVisible}
                      onChange={e => {
                        setPendingCols(prev =>
                          e.target.checked
                            ? [...prev, col.id]
                            : prev.filter(c => c !== col.id)
                        );
                      }}
                      className="rounded border-[#E5E5E5] text-[#0176D3] disabled:opacity-40"
                    />
                    <span className={col.alwaysVisible ? "text-[#706E6B]" : ""}>{col.label}</span>
                    {col.alwaysVisible && <span className="text-[9px] text-[#C9C9C9] ml-auto">fixed</span>}
                  </label>
                ))}
                <div className="flex gap-2 pt-2 border-t border-[#E5E5E5] mt-2">
                  <button
                    data-testid="save-column-prefs-btn"
                    disabled={colPrefSaving}
                    onClick={async () => {
                      setColPrefSaving(true);
                      try {
                        await userPrefsApi.saveListView("leads", { columns: pendingCols });
                        setVisibleCols(pendingCols);
                        setShowColPicker(false);
                      } catch { /* silent */ }
                      finally { setColPrefSaving(false); }
                    }}
                    className="flex-1 px-2 py-1.5 bg-[#EEF4FF] border border-[#0176D3]/30 rounded text-[10px] text-[#0176D3] font-medium"
                  >
                    {colPrefSaving ? "Saving…" : "Save"}
                  </button>
                  <button
                    data-testid="restore-default-cols-btn"
                    onClick={async () => {
                      try {
                        await userPrefsApi.deleteListView("leads");
                      } catch { /* silent */ }
                      setVisibleCols(DEFAULT_LEAD_COLS);
                      setPendingCols(DEFAULT_LEAD_COLS);
                      setShowColPicker(false);
                    }}
                    className="flex-1 px-2 py-1.5 border border-[#E5E5E5] rounded text-[10px] text-[#706E6B] hover:border-slate-500"
                  >
                    Default
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-40">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B]" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, phone, email, lead #..."
              data-testid="leads-search-input"
              className="w-full pl-8 pr-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#181818] placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3]/30"
            />
          </div>
          <Filter className="w-3.5 h-3.5 text-[#706E6B]" />
          <select
            value={filters.status}
            onChange={e => { setFilters(f => ({...f, status: e.target.value})); setPage(0); }}
            data-testid={LEADS.filterStatus}
            className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#444444] focus:outline-none focus:border-[#0176D3]/30">
            {STATUSES.map(s => <option key={s} value={s}>{s ? leadStatusLabel(s) : "All Statuses"}</option>)}
          </select>
          <select
            value={filters.qualification}
            onChange={e => { setFilters(f => ({...f, qualification: e.target.value})); setPage(0); }}
            data-testid={LEADS.filterQualification}
            className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#444444] focus:outline-none focus:border-[#0176D3]/30">
            {QUALIFICATIONS.map(q => <option key={q} value={q}>{q ? qualStatusLabel(q) : "All Qualification"}</option>)}
          </select>
          <select
            value={filters.source}
            onChange={e => { setFilters(f => ({...f, source: e.target.value})); setPage(0); }}
            className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#444444] focus:outline-none focus:border-[#0176D3]/30">
            {SOURCES.map(s => <option key={s} value={s}>{s || "All Sources"}</option>)}
          </select>
          <select
            value={filters.event_type_interest}
            onChange={e => { setFilters(f => ({...f, event_type_interest: e.target.value})); setPage(0); }}
            data-testid="filter-event-type"
            className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#444444] focus:outline-none focus:border-[#0176D3]/30">
            <option value="">All Event Types</option>
            {eventTypes.map(et => (
              <option key={et.id || et.name} value={et.name}>{et.name}</option>
            ))}
          </select>
          <select
            value={filters.planning_scope}
            onChange={e => { setFilters(f => ({...f, planning_scope: e.target.value})); setPage(0); }}
            data-testid="filter-planning-scope"
            className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#444444] focus:outline-none focus:border-[#0176D3]/30">
            <option value="">All Scopes</option>
            <option value="END_TO_END">End-to-End</option>
            <option value="CUSTOM">Custom</option>
          </select>
          {(filters.status || filters.qualification || filters.source || filters.event_type_interest || filters.planning_scope) && (
            <button
              onClick={() => { setFilters({ status: "", qualification: "", source: "", event_type_interest: "", planning_scope: "" }); setPage(0); }}
              className="text-xs text-[#706E6B] hover:text-[#444444] px-2 py-1 rounded border border-[#E5E5E5] hover:border-slate-500 transition-colors">
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid={LEADS.leadList}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#0176D3]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Target className="w-8 h-8 text-[#706E6B] mx-auto mb-3" />
            <p className="text-sm text-[#706E6B]">No leads found</p>
            <p className="text-xs text-[#706E6B] mt-1">Process inquiries from the Inbox or create a Lead manually</p>
            <button
              onClick={() => setShowNewLead(true)}
              className="mt-4 flex items-center gap-1.5 mx-auto px-4 py-2 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-xs text-[#0176D3] hover:bg-[#EEF4FF] transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Create First Lead
            </button>
          </div>
        ) : (
          <>
            {/* ── Mobile card list (< sm) ─────────────────────────────────── */}
            <div className="sm:hidden divide-y divide-[#E5E5E5]">
              {filtered.map(lead => (
                <div
                  key={lead.id}
                  data-testid={LEADS.leadRow}
                  onClick={() => navigate(`/engage/leads/${lead.id}`)}
                  className="flex items-start gap-3 px-4 py-3.5 cursor-pointer hover:bg-[#EEF4FF] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] font-mono text-[#0176D3]" data-testid={LEADS.leadNumber}>{lead.lead_number}</span>
                      <span data-testid={LEADS.leadStatus}
                        className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${STATUS_PILL[lead.status] || "text-[#706E6B] bg-slate-500/10 border-slate-500/20"}`}>
                        {leadStatusLabel(lead.status)}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-[#181818] truncate">{lead.customer_name || lead.contact_name || "—"}</p>
                    <p className="text-xs text-[#706E6B] mt-0.5">{lead.event_type_interest || "—"}
                      {lead.location ? ` · ${lead.location}` : ""}{lead.guest_count ? ` · ${lead.guest_count} guests` : ""}
                    </p>
                    {lead.planning_scope && (
                      <span className={`inline-block mt-1 text-[9px] font-mono px-1.5 py-0.5 rounded border ${lead.planning_scope === "END_TO_END" ? "bg-purple-500/10 border-purple-500/20 text-purple-700" : "bg-amber-500/10 border-amber-500/20 text-amber-700"}`}>
                        {lead.planning_scope === "END_TO_END" ? "End-to-End" : "Custom"}
                      </span>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#706E6B] flex-shrink-0 mt-1" />
                </div>
              ))}
            </div>
            {/* ── Desktop table (sm+) ─────────────────────────────────────── */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5E5E5] bg-white">
                    {LEADS_COL_REGISTRY.filter(c => visibleCols.includes(c.id)).map(c => (
                      <th key={c.id} className="text-left px-4 py-3 text-xs font-mono text-[#706E6B] uppercase tracking-wider">{c.label}</th>
                    ))}
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead, idx) => (
                    <tr
                      key={lead.id}
                      data-testid={LEADS.leadRow}
                      onClick={() => navigate(`/engage/leads/${lead.id}`)}
                      className={`border-b border-[#E5E5E5] cursor-pointer hover:bg-[#EEF4FF] transition-colors ${idx % 2 === 0 ? "" : "bg-white/30"}`}>
                      {visibleCols.includes("lead_number") && (
                        <td className="px-4 py-3">
                          <span className="text-xs font-mono text-[#0176D3]" data-testid={LEADS.leadNumber}>{lead.lead_number}</span>
                        </td>
                      )}
                      {visibleCols.includes("customer_name") && (
                        <td className="px-4 py-3">
                          <p className="text-[#181818] font-sans">{lead.customer_name || lead.contact_name || "—"}</p>
                          <p className="text-xs text-[#706E6B]">{lead.customer_email || lead.customer_contact_email || lead.contact_email || ""}</p>
                        </td>
                      )}
                      {visibleCols.includes("event_type_interest") && (
                        <td className="px-4 py-3">
                          <p className="text-[#444444]">{lead.event_type_interest || "—"}</p>
                          <p className="text-xs text-[#706E6B]">{lead.location ? `${lead.location}` : ""}</p>
                        </td>
                      )}
                      {visibleCols.includes("status") && (
                        <td className="px-4 py-3">
                          <span data-testid={LEADS.leadStatus}
                            className={`text-[10px] font-mono px-2 py-0.5 rounded border ${STATUS_PILL[lead.status] || "text-[#706E6B] bg-slate-500/10 border-slate-500/20"}`}>
                            {leadStatusLabel(lead.status)}
                          </span>
                        </td>
                      )}
                      {visibleCols.includes("qualification_status") && (
                        <td className="px-4 py-3">
                          <span data-testid={LEADS.leadQualification}
                            className={`text-xs font-mono ${QUAL_PILL[lead.qualification_status] || "text-[#706E6B]"}`}>
                            {qualStatusLabel(lead.qualification_status) || "—"}
                          </span>
                        </td>
                      )}
                      {visibleCols.includes("qualification_score") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B] font-mono">
                            {lead.qualification_score !== undefined ? `${lead.qualification_score}%` : "—"}
                          </span>
                        </td>
                      )}
                      {visibleCols.includes("source_channel") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B] bg-white border border-[#E5E5E5] px-1.5 py-0.5 rounded font-mono">
                            {lead.source_channel || "—"}
                          </span>
                        </td>
                      )}
                      {visibleCols.includes("location") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B]">{lead.location || "—"}</span>
                        </td>
                      )}
                      {visibleCols.includes("event_date") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B]">{lead.event_date ? new Date(lead.event_date).toLocaleDateString() : "—"}</span>
                        </td>
                      )}
                      {visibleCols.includes("guest_count") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B]">{lead.guest_count || "—"}</span>
                        </td>
                      )}
                      {visibleCols.includes("budget_range") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B]">{lead.budget_range || "—"}</span>
                        </td>
                      )}
                      {visibleCols.includes("assigned_poc_name") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B]">{lead.assigned_poc_name || "—"}</span>
                        </td>
                      )}
                      {visibleCols.includes("last_interaction_at") && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#706E6B]">{formatDate(lead.last_interaction_at || lead.created_at)}</span>
                        </td>
                      )}
                      <td className="px-4 py-3 text-right">
                        <ChevronRight className="w-4 h-4 text-[#706E6B] inline-block" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* ── end desktop table ───────────────────────────────────────── */}
            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E5E5]">
                <p className="text-xs text-[#706E6B]">
                  Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
                    className="px-3 py-1 text-xs border border-[#E5E5E5] rounded text-[#706E6B] hover:text-[#181818] hover:border-[#0176D3]/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    Prev
                  </button>
                  <button disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)}
                    className="px-3 py-1 text-xs border border-[#E5E5E5] rounded text-[#706E6B] hover:text-[#181818] hover:border-[#0176D3]/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* New Lead Modal */}
      {showNewLead && (
        <NewLeadModal
          onClose={() => setShowNewLead(false)}
          onCreated={handleLeadCreated}
        />
      )}
    </div>
  );
}

// ─── New Lead Modal ────────────────────────────────────────────────────────────

function NewLeadModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    salutation: "", first_name: "", last_name: "", email: "", phone: "",
    source_channel: "MANUAL", planning_scope: "END_TO_END",
    event_type_interest: "", event_date: "", guest_count: "", budget_range: "",
    location: "", venue_status: "", service_interests: [],
    city: "", state: "", country: "",
    decision_maker_identified: false,
    customer_responsive: false,
    notes: "",
  });
  const [loading,         setLoading]         = useState(false);
  const [saveAndNew,      setSaveAndNew]       = useState(false);
  const [pathConfig,      setPathConfig]       = useState(null);
  const [eventTypes,      setEventTypes]       = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [configLoading,   setConfigLoading]    = useState(true);
  const [section,         setSection]          = useState("lead");
  const [templateData,    setTemplateData]     = useState(null);
  const [templateLoading, setTemplateLoading]  = useState(false);
  const [fieldSources,    setFieldSources]     = useState({});
  const [descEdited,      setDescEdited]       = useState(false);

  useEffect(() => {
    Promise.all([
      engageApi.getLeadPathConfig(),
      eventModelApi.listEventTypes(),
      eventModelApi.listServiceCategories(),
    ]).then(([pc, et, sc]) => {
      setPathConfig(pc.data);
      setEventTypes((et.data || []).filter(t => t.status === "ACTIVE" || t.is_active));
      setServiceCategories(sc.data || []);
    }).catch(() => {}).finally(() => setConfigLoading(false));
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // ── Template auto-fill: trigger when event type OR planning scope changes ──
  useEffect(() => {
    if (!form.event_type_interest || form.planning_scope !== "END_TO_END") {
      setTemplateData(null);
      return;
    }
    let cancelled = false;
    setTemplateLoading(true);
    engageApi.getLeadPlanningTemplate(form.event_type_interest, form.planning_scope, {
      guest_count:  form.guest_count  || "",
      location:     form.location     || "",
      budget_range: form.budget_range || "",
      venue_status: form.venue_status || "",
      event_date:   form.event_date   || "",
    })
      .then(res => {
        if (cancelled) return;
        const tpl = res.data;
        setTemplateData(tpl);
        // Auto-select services from template (TEMPLATE_DEFAULT)
        if (tpl.services?.length > 0) {
          setForm(f => ({ ...f, service_interests: tpl.services }));
          setFieldSources(prev => ({ ...prev, service_interests: "TEMPLATE_DEFAULT" }));
        }
        // Auto-fill description if not yet edited by user
        if (tpl.description && !descEdited) {
          setForm(f => ({ ...f, notes: tpl.description }));
          setFieldSources(prev => ({ ...prev, notes: "SYSTEM_DERIVED" }));
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setTemplateLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.event_type_interest, form.planning_scope]);

  const [formErrors, setFormErrors] = useState({});

  const validate = () => {
    const errors = {};
    if (!form.first_name.trim() && !form.last_name.trim()) {
      errors.name = "First name or last name is required";
    }
    if (!form.email.trim() && !form.phone.trim()) {
      errors.contact = "At least one contact method (email or phone) is required";
    }
    if (!form.event_type_interest?.trim()) {
      errors.event_type_interest = "Event type is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submit = async (andNew = false) => {
    if (!validate()) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    setSaveAndNew(andNew);
    try {
      const payload = {
        ...form,
        guest_count: form.guest_count ? Number(form.guest_count) : null,
        event_date: form.event_date ? new Date(form.event_date).toISOString() : null,
        field_sources: fieldSources,
        planning_scope_services_source: fieldSources.service_interests || "POC_ENTERED",
      };
      const { data } = await leadApi.create(payload);
      toast.success(`Lead ${data.lead_number} created`);
      if (andNew) {
        setForm({
          salutation: "", first_name: "", last_name: "", email: "", phone: "",
          source_channel: "MANUAL", planning_scope: "END_TO_END",
          event_type_interest: "", event_date: "", guest_count: "", budget_range: "",
          location: "", venue_status: "", service_interests: [],
          city: "", state: "", country: "", notes: "",
        });
        setFormErrors({});
        setTemplateData(null);
        setFieldSources({});
        setDescEdited(false);
        setSection("lead");
      } else {
        onClose();
        onCreated(data);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errs = {};
        detail.forEach(d => { errs[d.field] = d.message; });
        setFormErrors(errs);
        toast.error("Please fix the highlighted fields");
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to create lead");
      }
    } finally {
      setLoading(false);
    }
  };

  const sourceChannels = pathConfig?.source_channels || [];
  const venueStatuses  = pathConfig?.venue_statuses || [];

  const SECTIONS = [
    { id: "lead",          label: "Lead Info" },
    { id: "event",         label: "Event Intent" },
    { id: "address",       label: "Address" },
    { id: "qualification", label: "Qualification" },
    { id: "desc",          label: "Description" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#F3F3F3]/85 p-0 sm:p-3 md:p-6">
      <div className="bg-white border border-[#E5E5E5] sm:rounded-xl w-full sm:max-w-3xl flex flex-col max-h-[96vh] sm:max-h-[92vh] rounded-t-2xl sm:rounded-t-xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[#E5E5E5] flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <Target className="w-4.5 h-4.5 text-[#0176D3]" />
            <h2 className="text-base font-semibold text-[#181818] font-sans">New Lead</h2>
          </div>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#444444] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Section tabs — scrollable on mobile */}
        <div className="flex border-b border-[#E5E5E5] px-3 sm:px-6 flex-shrink-0 overflow-x-auto">
          {SECTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className={`px-3 py-2.5 text-xs border-b-2 transition-colors whitespace-nowrap ${
                section === s.id
                  ? "border-[#0176D3] text-[#0176D3] font-medium"
                  : "border-transparent text-[#706E6B] hover:text-[#444444]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-5">
          {configLoading ? (
            <div className="space-y-3">
              {[1,2,3,4].map(i => <div key={i} className="h-10 bg-[#F3F3F3] rounded animate-pulse" />)}
            </div>
          ) : (
            <>
              {/* ── LEAD INFO ── */}
              {section === "lead" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                  <div>
                    <FormField label="First Name" required>
                      <input
                        value={form.first_name}
                        onChange={e => { set("first_name", e.target.value); setFormErrors(p => ({...p, name: ""})); }}
                        placeholder="First name"
                        data-testid="new-lead-first-name"
                        className={`${inputCls}${formErrors.name ? " border-rose-400 focus:ring-rose-300" : ""}`}
                      />
                    </FormField>
                    {formErrors.name && <p data-testid="err-lead-name" className="text-xs text-rose-500 mt-1">{formErrors.name}</p>}
                  </div>
                  <FormField label="Last Name">
                    <input
                      value={form.last_name}
                      onChange={e => { set("last_name", e.target.value); setFormErrors(p => ({...p, name: ""})); }}
                      placeholder="Last name"
                      data-testid="new-lead-last-name"
                      className={inputCls}
                    />
                  </FormField>
                  <div>
                    <FormField label="Email" required>
                      <input
                        type="email"
                        value={form.email}
                        onChange={e => { set("email", e.target.value); setFormErrors(p => ({...p, contact: ""})); }}
                        placeholder="customer@example.com"
                        data-testid="new-lead-email"
                        className={`${inputCls}${formErrors.contact ? " border-rose-400 focus:ring-rose-300" : ""}`}
                      />
                    </FormField>
                    {formErrors.contact && <p data-testid="err-lead-contact" className="text-xs text-rose-500 mt-1">{formErrors.contact}</p>}
                  </div>
                  <FormField label="Phone">
                    <input
                      value={form.phone}
                      onChange={e => { set("phone", e.target.value); setFormErrors(p => ({...p, contact: ""})); }}
                      placeholder="+91 …"
                      data-testid="new-lead-phone"
                      className={inputCls}
                    />
                  </FormField>
                  <FormField label="Source Channel">
                    <select
                      value={form.source_channel}
                      onChange={e => set("source_channel", e.target.value)}
                      data-testid="new-lead-source"
                      className={inputCls}
                    >
                      {sourceChannels.length > 0
                        ? sourceChannels.map(s => <option key={s.code} value={s.code}>{s.label}</option>)
                        : <option value="MANUAL">Manual Entry</option>
                      }
                    </select>
                  </FormField>
                  <FormField label="Planning Scope">
                    <PlanningScope
                      value={form.planning_scope}
                      onChange={v => { set("planning_scope", v); setTemplateData(null); }}
                    />
                  </FormField>
                  {/* CRM-UX-01 Stage 01: Event Type promoted to Lead Info so all required fields are on section 1 */}
                  <div>
                    <FormField label="Event Type" required>
                      <select
                        value={form.event_type_interest}
                        onChange={e => { set("event_type_interest", e.target.value); setFormErrors(p => ({...p, event_type_interest: ""})); }}
                        data-testid="new-lead-event-type-quick"
                        className={`${inputCls}${formErrors.event_type_interest ? " border-rose-400" : ""}`}
                      >
                        <option value="">— Select Event Type —</option>
                        {eventTypes.length > 0
                          ? eventTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)
                          : <option disabled>No event types configured</option>
                        }
                      </select>
                    </FormField>
                    {formErrors.event_type_interest && <p data-testid="err-lead-event-type" className="text-xs text-rose-500 mt-1">{formErrors.event_type_interest}</p>}
                  </div>
                </div>
              )}

              {/* ── EVENT INTENT ── */}
              {section === "event" && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                    <div>
                      <FormField label="Event Type" required>
                        <select
                          value={form.event_type_interest}
                          onChange={e => { set("event_type_interest", e.target.value); setFormErrors(p => ({...p, event_type_interest: ""})); }}
                          data-testid="new-lead-event-type"
                          className={`${inputCls}${formErrors.event_type_interest ? " border-rose-400" : ""}`}
                        >
                          <option value="">— Select Event Type —</option>
                          {eventTypes.length > 0
                            ? eventTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)
                            : <option disabled>No event types configured</option>
                          }
                        </select>
                      </FormField>
                      {formErrors.event_type_interest && <p data-testid="err-lead-event-type" className="text-xs text-rose-500 mt-1">{formErrors.event_type_interest}</p>}
                    </div>
                    <FormField label="Tentative Date">
                      <input
                        type="date"
                        value={form.event_date}
                        onChange={e => set("event_date", e.target.value)}
                        data-testid="new-lead-event-date"
                        className={inputCls}
                      />
                    </FormField>
                    <FormField label="Guest Count">
                      <input
                        type="number"
                        min="1"
                        value={form.guest_count}
                        onChange={e => set("guest_count", e.target.value)}
                        placeholder="e.g. 150"
                        data-testid="new-lead-guest-count"
                        className={inputCls}
                      />
                    </FormField>
                    <FormField label="Budget Range">
                      <input
                        value={form.budget_range}
                        onChange={e => set("budget_range", e.target.value)}
                        placeholder="e.g. ₹5L – ₹10L"
                        data-testid="new-lead-budget"
                        className={inputCls}
                      />
                    </FormField>
                    <FormField label="Location / City">
                      <input
                        value={form.location}
                        onChange={e => set("location", e.target.value)}
                        placeholder="City or venue area"
                        data-testid="new-lead-location"
                        className={inputCls}
                      />
                    </FormField>
                    <FormField label="Venue Status">
                      <select
                        value={form.venue_status}
                        onChange={e => set("venue_status", e.target.value)}
                        data-testid="new-lead-venue-status"
                        className={inputCls}
                      >
                        <option value="">— Not specified —</option>
                        {venueStatuses.map(v => <option key={v.code} value={v.code}>{v.label}</option>)}
                      </select>
                    </FormField>
                  </div>

                  {/* Services of Interest */}
                  {serviceCategories.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[11px] text-[#706E6B] uppercase tracking-wider">Services of Interest</p>
                        {fieldSources.service_interests === "TEMPLATE_DEFAULT" && (
                          <span className="text-[9px] font-semibold bg-[#EEF4FF] text-[#0176D3] px-2 py-0.5 rounded-full">
                            TEMPLATE DEFAULT
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {serviceCategories.map(s => {
                          const active = form.service_interests.includes(s.name);
                          return (
                            <button
                              key={s.id || s.name}
                              type="button"
                              onClick={() => {
                                setForm(f => ({
                                  ...f,
                                  service_interests: f.service_interests.includes(s.name)
                                    ? f.service_interests.filter(x => x !== s.name)
                                    : [...f.service_interests, s.name],
                                }));
                                setFieldSources(prev => ({ ...prev, service_interests: "POC_ENTERED" }));
                              }}
                              data-testid={`service-chip-${s.name.toLowerCase().replace(/\s+/g,"-")}`}
                              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                                active
                                  ? "bg-[#EEF4FF] border-[#0176D3]/30 text-[#0176D3]"
                                  : "border-[#E5E5E5] text-[#706E6B] hover:border-slate-500 hover:text-[#444444]"
                              }`}
                            >
                              {s.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* END-TO-END RECOMMENDATION PANEL */}
                  {form.event_type_interest && form.planning_scope === "END_TO_END" && (
                    <TemplateRecommendationPanel
                      templateData={templateData}
                      loading={templateLoading}
                      eventType={form.event_type_interest}
                      form={form}
                    />
                  )}
                </div>
              )}

              {/* ── ADDRESS ── */}
              {section === "address" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                  <FormField label="City">
                    <input
                      value={form.city}
                      onChange={e => set("city", e.target.value)}
                      placeholder="City"
                      className={inputCls}
                    />
                  </FormField>
                  <FormField label="State / Region">
                    <input
                      value={form.state}
                      onChange={e => set("state", e.target.value)}
                      placeholder="State"
                      className={inputCls}
                    />
                  </FormField>
                  <FormField label="Country">
                    <input
                      value={form.country}
                      onChange={e => set("country", e.target.value)}
                      placeholder="Country"
                      className={inputCls}
                    />
                  </FormField>
                </div>
              )}

              {/* ── QUALIFICATION ── */}
              {section === "qualification" && (
                <QualificationSection
                  form={form}
                  set={set}
                  criteria={pathConfig?.qualification_criteria || []}
                  flags={pathConfig?.qualification_flags || []}
                  thresholds={pathConfig?.qualification_thresholds || {}}
                  templateData={templateData}
                />
              )}

              {/* ── DESCRIPTION ── */}
              {section === "desc" && (
                <div className="space-y-3">
                  {fieldSources.notes === "SYSTEM_DERIVED" && !descEdited && (
                    <div className="flex items-center gap-2 p-2.5 bg-[#F9F9F9] rounded-lg border border-[#E5E5E5]">
                      <span className="text-[9px] font-bold text-[#0176D3] bg-[#EEF4FF] px-2 py-0.5 rounded-full shrink-0">SYSTEM DERIVED</span>
                      <span className="text-[11px] text-[#706E6B]">Auto-generated from event type and available information. Edit to customise.</span>
                    </div>
                  )}
                  <FormField label="Notes / Description">
                    <textarea
                      value={form.notes}
                      onChange={e => { set("notes", e.target.value); setDescEdited(true); setFieldSources(p => ({ ...p, notes: "POC_ENTERED" })); }}
                      rows={6}
                      placeholder="Add context, notes or inquiry description…"
                      data-testid="new-lead-notes"
                      className={`${inputCls} resize-none`}
                    />
                  </FormField>
                  {templateData?.next_questions?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-[#706E6B] mb-2">
                        Information Still Needed
                      </p>
                      <ul className="space-y-1">
                        {templateData.needs_confirmation.map(item => (
                          <li key={item} className="flex items-center gap-2 text-xs text-[#444444]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#FE9339] shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                      <p className="text-[10px] font-semibold uppercase tracking-widest text-[#706E6B] mt-3 mb-2">
                        Next Recommended Questions
                      </p>
                      <ul className="space-y-1">
                        {templateData.next_questions.map(q => (
                          <li key={q} className="flex items-start gap-2 text-xs text-[#706E6B]">
                            <span className="mt-1 shrink-0 text-[#0176D3]">›</span>
                            {q}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#E5E5E5] px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 flex-shrink-0 flex-wrap">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm text-[#706E6B] hover:text-[#181818] border border-[#E5E5E5] rounded-lg hover:border-slate-500 transition-colors min-h-[44px]"
          >
            Cancel
          </button>
          <div className="flex items-center gap-2">
            <button
              data-testid="new-lead-save-and-new"
              onClick={() => submit(true)}
              disabled={loading}
              className="px-3 py-2.5 text-sm border border-[#E5E5E5] rounded-lg text-[#706E6B] hover:text-[#181818] hover:border-slate-500 disabled:opacity-60 transition-colors min-h-[44px]"
            >
              {loading && saveAndNew ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
              Save & New
            </button>
            <button
              data-testid="new-lead-save"
              onClick={() => submit(false)}
              disabled={loading}
              className="px-5 py-2.5 text-sm bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-[#0176D3] hover:bg-[#EEF4FF] disabled:opacity-60 transition-colors font-medium min-h-[44px]"
            >
              {loading && !saveAndNew ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Qualification Section ──────────────────────────────────────────────────────

function QualificationSection({ form, set, criteria, flags, thresholds, templateData }) {
  // Compute live qualification score from form state — logic from backend config
  const score = criteria.reduce((sum, c) => {
    const val = form[c.field];
    const filled = Array.isArray(val) ? val.length > 0 : Boolean(val);
    return filled ? sum + (c.weight || 0) : sum;
  }, 0);

  const QUALIFIED_THRESHOLD  = thresholds.QUALIFIED           || 60;
  const PARTIAL_THRESHOLD    = thresholds.PARTIALLY_QUALIFIED || 30;
  const tplReadiness = templateData?.readiness_score ?? null;

  const classification =
    score >= QUALIFIED_THRESHOLD  ? { label: "Qualified",            color: "text-[#2E844A] bg-emerald-400/10 border-emerald-400/30" } :
    score >= PARTIAL_THRESHOLD    ? { label: "Partially Qualified",  color: "text-[#8C4B00]  bg-amber-400/10  border-amber-400/30"  } :
                                    { label: "Unqualified",          color: "text-[#706E6B]  bg-slate-400/10  border-slate-400/20"  };

  // Field status for "Known / Recommended / Needs Confirmation" table
  const FIELD_MAP = [
    { key: "event_type_interest", label: "Event Type" },
    { key: "planning_scope",      label: "Planning Scope" },
    { key: "guest_count",         label: "Guest Count" },
    { key: "event_date",          label: "Event Date" },
    { key: "budget_range",        label: "Budget Range" },
    { key: "location",            label: "Location" },
    { key: "venue_status",        label: "Venue Status" },
    { key: "service_interests",   label: "Services" },
  ];
  const needsConf = new Set(
    (templateData?.needs_confirmation || []).map(s => s.toLowerCase())
  );
  const getStatus = (key) => {
    const val = form[key];
    const filled = Array.isArray(val) ? val.length > 0 : Boolean(val);
    if (!filled) {
      const label = FIELD_MAP.find(f => f.key === key)?.label?.toLowerCase() || key;
      if (needsConf.has(label)) return { text: "Needs Confirmation", cls: "text-[#FE9339]" };
      return { text: "Missing", cls: "text-[#706E6B]" };
    }
    if (key === "service_interests" && templateData) return { text: "Recommended", cls: "text-[#0176D3]" };
    if (key === "planning_scope")    return { text: "Known", cls: "text-[#2E844A]" };
    if (key === "event_type_interest") return { text: "Known", cls: "text-[#2E844A]" };
    return { text: "Known", cls: "text-[#2E844A]" };
  };

  return (
    <div className="space-y-5">
      {/* Planning Readiness */}
      <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-[#706E6B] uppercase tracking-wider">Planning Readiness</p>
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${classification.color}`}>
            {classification.label}
          </span>
        </div>
        {tplReadiness !== null && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-[#706E6B]">Planning setup</span>
              <span className="text-sm font-bold text-[#0176D3]">{tplReadiness}% prepared</span>
            </div>
            <div className="h-2 bg-[#E5E5E5] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-[#0176D3] transition-all duration-700"
                style={{ width: `${tplReadiness}%` }}
              />
            </div>
          </div>
        )}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-1.5 bg-[#E5E5E5] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(score, 100)}%`,
                background: score >= QUALIFIED_THRESHOLD ? "#34D399" :
                            score >= PARTIAL_THRESHOLD  ? "#F59E0B" : "#444444"
              }}
            />
          </div>
          <span className="text-sm font-bold text-[#181818] w-8 text-right font-mono">{score}</span>
        </div>
        <p className="text-[10px] text-[#706E6B] mt-2">
          Qualification score based on filled fields. Fill Event Intent fields to increase.
        </p>
      </div>

      {/* Field Status Table */}
      <div>
        <p className="text-[11px] text-[#706E6B] uppercase tracking-wider mb-2">Field Status</p>
        <div className="rounded-lg border border-[#E5E5E5] overflow-hidden">
          <table className="w-full text-xs">
            <tbody className="divide-y divide-[#F3F3F3]">
              {FIELD_MAP.map(({ key, label }) => {
                const { text, cls } = getStatus(key);
                return (
                  <tr key={key} className="px-3 py-2">
                    <td className="px-3 py-2 text-[#444444]">{label}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${cls}`}>{text}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Criteria Checklist */}
      <div className="space-y-1.5">
        <p className="text-[11px] text-[#706E6B] uppercase tracking-wider mb-2">Scoring Criteria</p>
        {criteria.map(c => {
          const val = form[c.field];
          const filled = Array.isArray(val) ? val.length > 0 : Boolean(val);
          return (
            <div key={c.code} className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${filled ? "bg-emerald-400" : "bg-[#E5E5E5]"}`} />
                <span className={`text-xs ${filled ? "text-[#444444]" : "text-[#706E6B]"}`}>{c.label}</span>
              </div>
              <span className={`text-[10px] font-mono ${filled ? "text-[#2E844A]" : "text-[#706E6B]"}`}>
                {filled ? `+${c.weight}` : c.weight}
              </span>
            </div>
          );
        })}
      </div>

      {/* Manual Qualification Flags */}
      {flags.length > 0 && (
        <div className="space-y-3">
          <p className="text-[11px] text-[#706E6B] uppercase tracking-wider">Manual Flags</p>
          {flags.map(f => (
            <label key={f.code} className="flex items-center gap-3 cursor-pointer">
              <button
                type="button"
                onClick={() => set(f.code, !form[f.code])}
                className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                  form[f.code]
                    ? "bg-[#EEF4FF] border-[#0176D3]/30"
                    : "border-[#E5E5E5] bg-transparent"
                }`}
              >
                {form[f.code] && <span className="w-2 h-2 bg-[#0176D3] rounded-sm" />}
              </button>
              <span className="text-sm text-[#444444]">{f.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Form Primitives ───────────────────────────────────────────────────────────

const inputCls = "w-full px-3 py-2 bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg text-sm text-[#181818] placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3]/30 transition-colors";

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="block text-[11px] text-[#706E6B] uppercase tracking-wider mb-1.5">
        {label}{required && <span className="text-[#0176D3] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

// ── Planning Scope Segmented Control ─────────────────────────────────────────

function PlanningScope({ value, onChange }) {
  return (
    <div
      data-testid="new-lead-planning-scope"
      className="flex rounded-lg border border-[#E5E5E5] overflow-hidden h-9"
    >
      {[
        { code: "END_TO_END", label: "End-to-End" },
        { code: "CUSTOM",     label: "Custom"     },
      ].map(opt => (
        <button
          key={opt.code}
          type="button"
          onClick={() => onChange(opt.code)}
          data-testid={`planning-scope-${opt.code.toLowerCase()}`}
          className={`flex-1 text-xs font-medium transition-colors ${
            value === opt.code
              ? "bg-[#0176D3] text-white"
              : "bg-white text-[#706E6B] hover:bg-[#F3F3F3]"
          }`}
        >
          {value === opt.code ? "✓ " : ""}{opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Template Recommendation Panel ────────────────────────────────────────────

function TemplateRecommendationPanel({ templateData, loading, eventType }) {
  if (loading) {
    return (
      <div className="bg-[#F0F7FF] border border-[#0176D3]/20 rounded-xl p-4 flex items-center gap-3">
        <div className="w-4 h-4 border-2 border-[#0176D3] border-t-transparent rounded-full animate-spin shrink-0" />
        <span className="text-xs text-[#0176D3]">Loading End-to-End recommendations…</span>
      </div>
    );
  }
  if (!templateData) return null;

  const services = templateData.services || [];
  const readiness = templateData.readiness_score ?? 0;
  const needsConf = templateData.needs_confirmation || [];

  return (
    <div
      data-testid="template-recommendation-panel"
      className="bg-[#F0F7FF] border border-[#0176D3]/25 rounded-xl p-4 space-y-4"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#0176D3]">
            End-to-End Recommendation
          </p>
          <p className="text-sm font-semibold text-[#181818] mt-0.5">{eventType}</p>
        </div>
        <div className="shrink-0 text-right">
          <span
            data-testid="readiness-score-badge"
            className="text-xl font-bold text-[#0176D3]"
          >{readiness}%</span>
          <p className="text-[9px] text-[#706E6B] mt-0">prepared</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-[#D9EBFF] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-[#0176D3] transition-all duration-700"
          style={{ width: `${readiness}%` }}
        />
      </div>

      {/* Services auto-selected */}
      {services.length > 0 && (
        <div>
          <p className="text-[10px] text-[#0176D3] font-semibold mb-2">
            {services.length} standard services selected automatically
          </p>
          <div className="flex flex-wrap gap-1.5">
            {services.map(s => (
              <span
                key={s}
                data-testid={`tpl-service-${s.toLowerCase().replace(/\s+/g,"-")}`}
                className="flex items-center gap-1 text-[11px] bg-white border border-[#0176D3]/20 text-[#0176D3] px-2 py-1 rounded-md"
              >
                <span className="text-[#2E844A]">✓</span> {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Needs confirmation */}
      {needsConf.length > 0 && (
        <div>
          <p className="text-[10px] text-[#706E6B] font-semibold uppercase tracking-wide mb-1.5">
            Needs confirmation
          </p>
          <div className="flex flex-col gap-0.5">
            {needsConf.map(item => (
              <div key={item} className="flex items-center gap-1.5 text-[11px] text-[#444444]">
                <span className="w-1 h-1 rounded-full bg-[#FE9339] shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[9px] text-[#706E6B]">
        Source: TEMPLATE_DEFAULT · Tier T0 · v{templateData.template_version ?? "1.0"} ·
        Services can be modified above
      </p>
    </div>
  );
}

// ── Utilities ─────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" });
  } catch { return "—"; }
}
